import React, { Dispatch, SetStateAction, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  IconButton,
  Collapse,
  Divider,
  FormGroup,
  useTheme,
  useMediaQuery,
  Grid2
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  NotificationDataType,
  NotificationSettingOptions
} from "../Domain/NotificationSettings.types";
import { NotificationCardDescription } from "./NotificationCardDescription.partial";

export interface NotificationCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  items: NotificationSettingOptions[];
  switches: Record<string, boolean>;
  setSwitches: Dispatch<SetStateAction<Record<string, boolean>>>;
  timeSwitches?: Record<string, number>;
  setTimeSwitches?: Dispatch<SetStateAction<Record<string, number>>>;
  /** Rendered below flat notification rows inside the expanded card (e.g. service location tree mock). */
  appendContent?: React.ReactNode;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  title,
  icon,
  color,
  items,
  switches,
  setSwitches,
  timeSwitches,
  setTimeSwitches,
  appendContent
}) => {
  const { t, i18n } = useTranslation();

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [expanded, setExpanded] = useState<boolean>(false);
  const [descExpanded, setDescExpanded] = useState<Record<number, boolean>>(() =>
    items.reduce<Record<number, boolean>>((acc, _, index) => ({ ...acc, [index]: false }), {})
  );

  const handleToggle = (index: string) => {
    const wasOn = switches[index]; // current state before toggle

    setSwitches(prev => ({ ...prev, [index]: !prev[index] }));

    // when turning from ON → OFF, reset its time back to 10
    if (wasOn) setTimeSwitches?.(prev => ({ ...prev, [index]: 10 }));
  };

  const handleDescToggle = (index: number) =>
    setDescExpanded(prev => ({ ...prev, [index]: !prev[index] }));

  const handleEditTime = (subject: NotificationDataType, newValue: number) => {
    setTimeSwitches?.(prev => ({ ...prev, [subject]: newValue }));
  };

  if (items.length === 0 && appendContent === undefined) return null;

  return (
    <Card sx={{ mb: 3, backgroundColor: color, borderRadius: 2 }}>
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{ cursor: "pointer" }}
        role="button"
        data-testid="notification-subject-card-box-parent-clickable"
        aria-label="Toggle notification details"
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
          data-testid="notification-subject-card-box-parent"
        >
          <Box
            sx={{ overflow: "hidden", display: "flex", alignItems: "center" }}
            data-testid="notification-subject-card-box-header"
          >
            {icon}
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.mode === "light" ? "black" : "white",
                ml: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "70vw"
              }}
            >
              {t(title)}
            </Typography>
          </Box>

          <IconButton
            data-testid="notification-subject-card-header-expand-btn"
            sx={[
              {
                transition: "transform 0.3s",
                color: theme.palette.mode === "light" ? "black" : "white"
              },
              expanded ? { transform: "rotate(0deg)" } : { transform: "rotate(-90deg)" }
            ]}
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ py: "0px !important" }}>
          {items.length > 0 && (
            <FormGroup>
              {items.map((item, index) => {
                const itemName = item.name;
                let itemLabel = i18n.language === "en" ? item.en : item.de;
                if (itemName === NotificationDataType.TEAM_BOOKING) {
                  itemLabel = i18n.language === "en" ? "Team Booking" : "Teambuchung";
                }

                return (
                  <Box
                    key={itemName}
                    data-testid="notification-form-box"
                    sx={[
                      { placeContent: "center", minHeight: 55, py: 1.5 },
                      isMobileOrTablet ? { maxHeight: "auto" } : { maxHeight: "fit-content" },
                      index === items.length - 1
                        ? { borderBottom: "none" }
                        : { borderBottom: `1px solid ${theme.palette.divider}` }
                    ]}
                  >
                    <Box display="flex" alignItems="center">
                      <Switch
                        data-testid={`notification-subject-card-${itemName}-switch`}
                        checked={switches[itemName]}
                        onChange={() => handleToggle(itemName)}
                      />

                      <Grid2
                        container
                        sx={{ ml: 1, alignItems: "center", display: "flex", width: "100%" }}
                        data-testid="notification-form-box-child"
                      >
                        <Grid2 size={{ xs: 10, md: 2.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: switches[itemName] ? 600 : 400, lineHeight: "1.45" }}
                          >
                            {/**
                             * itemLabel is the label of the item, it is used to display the label of the item in the notification subject card
                             * use text from the BE, however, 'Supervisor' only needs to be translated to 'Vorgesetze', rather than 'Vorgesetzer'
                             */}
                            {t(itemLabel)}
                          </Typography>
                        </Grid2>

                        <Grid2
                          data-testid="notification-form-box-desc"
                          size={{ xs: 2, md: 9.5 }}
                          sx={[
                            { display: "flex", alignItems: "center", minHeight: 50 },
                            isMobileOrTablet ? { maxHeight: "auto" } : { maxHeight: "fit-content" }
                          ]}
                        >
                          {!isMobileOrTablet && (
                            <>
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ my: 0, ml: 1, mr: 2, opacity: "0.6" }}
                              />
                              <NotificationCardDescription
                                subject={itemName as NotificationDataType}
                                time={10}
                                timeSwitches={timeSwitches}
                                isActivated={switches[itemName]}
                                handleEditTime={handleEditTime}
                              />
                            </>
                          )}
                          {isMobileOrTablet && (
                            <IconButton
                              onClick={() => handleDescToggle(index)}
                              data-testid={`notification-subject-card-itemDesc-expand-btn${index}`}
                              sx={{
                                transform: descExpanded[index] ? "rotate(0deg)" : "rotate(-90deg)",
                                transition: "transform 0.3s"
                              }}
                            >
                              <ExpandMore />
                            </IconButton>
                          )}
                        </Grid2>
                      </Grid2>
                    </Box>
                    <Collapse in={descExpanded[index]} unmountOnExit>
                      <Box sx={{ p: "8px 50px 15px 65px" }}>
                        <NotificationCardDescription
                          subject={itemName as NotificationDataType}
                          time={10}
                          timeSwitches={timeSwitches}
                          isActivated={switches[itemName]}
                          handleEditTime={handleEditTime}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </FormGroup>
          )}
          {appendContent}
        </CardContent>
      </Collapse>
    </Card>
  );
};
